/**
 * Derives which FLK form the candidate is on.
 *
 * TFN, Super and Dynamic used to be detected by repeating the same three-way
 * branch in every function that needed it, and `type` / `formIndex` were kept in
 * state by two different effects — so they could describe two different forms at
 * once, which put the Edit button on the wrong page.
 *
 * There is only ever one active form, so read the answer off it instead of
 * searching by type. Searching by type is what made every Edit on a dynamic form
 * open the *first* dynamic form in the flow rather than the one in front of you.
 */

export type FlkFormType = "TFN" | "SUPER" | "DYNAMIC" | null;

export type FlkFormDescriptor = {
    type: FlkFormType;
    /** Position in updateFormData; null when the active form is not an FLK form. */
    formIndex: number | null;
    /** flk_config.dynamicFormName — the key the backend stores this document under. */
    formName: string;
    /** flk_config.className, for the per-form body theme. */
    className: string | null;
    /** flk_config.skipDualForm */
    skipDualForm: boolean;
};

export const EMPTY_FLK_DESCRIPTOR: FlkFormDescriptor = {
    type: null,
    formIndex: null,
    formName: "",
    className: null,
    skipDualForm: false,
};

/** The FLK type of a single form row, or null if it is not an FLK form. */
export const getFlkTypeForForm = (form: any): FlkFormType => {
    if (!form) { return null; }
    if (form.isTFNForm === 1) { return "TFN"; }
    if (form.isSuperForm === 1) { return "SUPER"; }
    if (form.flk_config?.isDynamicFlkForm) { return "DYNAMIC"; }
    return null;
};

/**
 * Whether this form row already has a signed FLK document, according to the last
 * candidate-data fetch.
 *
 * Nested keys are not camel-cased by the API's response middleware (it only walks
 * the top level), so the row carries several spellings of the same thing. Read all
 * of them rather than betting on one.
 *
 * This matters for `skipDualForm` forms: they submit themselves on arrival, so
 * without this check, navigating back to an already-signed one re-submits it, the
 * backend advances the page because the document is signed, and the candidate is
 * bounced straight back to the form they came from.
 */
export const hasSignedFlkDocument = (form: any): boolean => {
    if (!form) { return false; }

    const type = getFlkTypeForForm(form);

    if (type === "TFN") {
        return Boolean(form.FlkPdfDoc) || form.FlkDocStatus === "complete";
    }

    if (type === "SUPER") {
        return Boolean(form.FlkSuperPdfDoc) || form.FlkSuperDocStatus === "complete";
    }

    if (type === "DYNAMIC") {
        return Boolean(
            form.is_dynamic_signed ||
            form.dynamic_signed_url ||
            form.DynamicFlkSignedUrl ||
            form.DynamicFlkPdfDoc
        ) || form.DynamicFlkDocStatus === "complete";
    }

    return false;
};

/** Describes one form row at a known index. */
export const describeFlkForm = (form: any, index: number | null): FlkFormDescriptor => {
    const type = getFlkTypeForForm(form);

    if (!type) { return EMPTY_FLK_DESCRIPTOR; }

    return {
        type,
        formIndex: index,
        formName: form?.flk_config?.dynamicFormName || "",
        className: form?.flk_config?.className || null,
        skipDualForm: Boolean(form?.flk_config?.skipDualForm),
    };
};

/**
 * Describes the FLK form the candidate is currently on.
 * Returns the empty descriptor when the active form is not an FLK form.
 */
export const describeActiveFlkForm = (formData: any[]): FlkFormDescriptor => {
    if (!Array.isArray(formData) || formData.length === 0) {
        return EMPTY_FLK_DESCRIPTOR;
    }

    const activeIndex = formData.findIndex((form: any) => form?.active);

    if (activeIndex === -1) { return EMPTY_FLK_DESCRIPTOR; }

    // Prefer the row's own index property where it exists, since the rest of the
    // component keys form refs and DOM ids off it.
    const form = formData[activeIndex];
    const index = typeof form?.index === "number" ? form.index : activeIndex;

    return describeFlkForm(form, index);
};
